/**
 * Widget Wrapper - Contenedor base para widgets
 *
 * Proporciona estilo consistente, estado de carga, y manejo de errores.
 */

import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ErrorOutlineIcon, HourglassEmptyIcon, RefreshIcon } from "../../../icons";
export interface WidgetWrapperProps {
  title: string;
  loading?: boolean;
  /** El server está computando en background (respuesta 202): relojito. */
  computing?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  children: React.ReactNode;
  minHeight?: number;
  icon?: React.ReactNode;
}

export const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  title,
  loading = false,
  computing = false,
  error = null,
  onRefresh,
  children,
  minHeight = 150,
  icon,
}) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        avatar={icon}
        title={
          <Typography variant="subtitle1" fontWeight="medium">
            {title}
          </Typography>
        }
        action={
          onRefresh ? (
            <Tooltip title="Actualizar">
              <IconButton size="small" onClick={onRefresh} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null
        }
        sx={{ pb: 0 }}
      />
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight,
        }}
      >
        {loading ? (
          <CircularProgress size={32} />
        ) : computing ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'text.secondary',
            }}
          >
            <HourglassEmptyIcon
              sx={{
                fontSize: 32,
                mb: 1,
                animation: 'hourglassSpin 2.4s ease-in-out infinite',
                '@keyframes hourglassSpin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '80%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(180deg)' },
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Calculando…
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'error.main',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="caption" color="error" textAlign="center">
              {error}
            </Typography>
          </Box>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
};

export default WidgetWrapper;
